import {
    ChartContainer,
    ChartWrapper,
    HeaderWrapper,
    ReportingObjectType,
    VisitorsAtTheMomentWrapper,
    VisitorsCount,
    VisitorsInsideCurrentTime,
    Wrapper,
} from './styles';
import { useEffect, useMemo, useRef, useState } from 'react';

import { DateTime } from 'luxon';
import Dynamics from '../../../../../Charts/Dynamics/Dynamics';
import { IShowSharedTooltips } from './interfaces';
import WidgetTitle from '../../../../../Wrappers/WidgetTitle/WidgetTitle';
import { generalReducerValues } from '../../../../../../../General.reducer';
import getVisitorsAtTheMoment from './tools/getVisitorsAtTheMoment';
import usePrepareData from './hooks/usePrepareData';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useWidgetCurrentOptions } from '../../../../../../../tools/useWidgetCurrentOptions';
import { Performance_VisitorsInside_Widget_Reducer_Values, reloadWidget } from './reducer';
import { withLoading } from '../../../../../../../tools/API/withLoading';
import { ResponseStatus } from '../../../../../../../tools/API/constants';
import WidgetAdditionalControls from '../../../../../WidgetAdditionalControls/WidgetAdditionalControls';

/**
 * Виджет для отображения посетителей внутри
 */
const VisitorsInside = () => {
    const { extendedReportingObjectsById, reportingObjectsRawMetricsDataById } = useSelector(
        Performance_VisitorsInside_Widget_Reducer_Values,
    );
    const localCurrentOptions = useWidgetCurrentOptions('Performance:Visitors inside');
    const [showSharedTooltips, setShowSharedTooltips] = useState<IShowSharedTooltips>({
        containerId: null,
        show: false,
        index: null,
    });
    const [chartsIntsance, setChartsInstane] = useState<Highcharts.Chart[]>([]);
    const { lang } = useSelector(generalReducerValues);
    const { t } = useTranslation();
    const itemsRef = useRef<Array<HTMLDivElement | null>>([]);
    usePrepareData();

    /**
     * Одновременное отображение нескольких тултипов
     */
    useEffect(() => {
        if (showSharedTooltips.show) {
            chartsIntsance.forEach((instance) => {
                if (showSharedTooltips.containerId !== instance?.container?.id) {
                    if (instance.series?.length) {
                        const tooltipsToUpdate = instance.series
                            .map((element) => element.data[showSharedTooltips.index || 0])
                            ?.filter((element) => element?.y !== null && element?.y !== undefined);

                        tooltipsToUpdate.length && instance.tooltip.refresh(tooltipsToUpdate);
                    }
                }
            });
        } else {
            chartsIntsance.length && chartsIntsance.forEach((instance) => instance.tooltip?.hide());
        }
    }, [showSharedTooltips]);

    /**
     * Сохранение экземпляров класса графиков для работы с тултипами
     */
    const storeChartInstance = (chart: Highcharts.Chart) => {
        const chartInstance = chartsIntsance?.find((element) => element?.container?.id === chart.container.id);
        if (!chartInstance) {
            setChartsInstane((prevState) => {
                prevState.push(chart);
                return prevState;
            });
        }
    };

    const visitorsInsideText = t('visitors inside in');

    const charts = useMemo(() => {
        return Object.keys(extendedReportingObjectsById).map((key, i) => {
            const { objectInfo, chartOptionsGeneratorSettings } = extendedReportingObjectsById[key];

            const WithLoadingTable = withLoading(ChartContainer, {
                data: reportingObjectsRawMetricsDataById[key] || {
                    status: ResponseStatus.Loading,
                    message: t('Loading...'),
                },
                height: 400,
            });

            return (
                <ChartWrapper key={objectInfo?.id} ref={(el) => (itemsRef.current[i] = el)}>
                    <HeaderWrapper>
                        <WidgetTitle>
                            <ReportingObjectType>{t(objectInfo?.object_type)}</ReportingObjectType>
                            {objectInfo?.name}
                            <WidgetAdditionalControls
                                pdfDownloadData={{ targetRef: { current: itemsRef.current[i] }, rerenderKey: 0 }}
                                widgetName={'Visitors inside'}
                                reloadHandler={i === 0 ? reloadWidget : undefined}
                            />
                        </WidgetTitle>
                        {localCurrentOptions?.mainPeriod?.id === 'today' ? (
                            chartOptionsGeneratorSettings ? (
                                <VisitorsAtTheMomentWrapper>
                                    <VisitorsCount>
                                        {getVisitorsAtTheMoment(chartOptionsGeneratorSettings.series)}
                                    </VisitorsCount>
                                    <VisitorsInsideCurrentTime>
                                        {`${visitorsInsideText} ${DateTime.now()
                                            .setZone(chartOptionsGeneratorSettings.timezone)
                                            .toFormat('HH:mm')}`}
                                    </VisitorsInsideCurrentTime>
                                </VisitorsAtTheMomentWrapper>
                            ) : null
                        ) : null}
                    </HeaderWrapper>
                    <WithLoadingTable>
                        {chartOptionsGeneratorSettings && (
                            <Dynamics
                                toggleShowSharedTooltips={setShowSharedTooltips}
                                storeChartInstance={storeChartInstance}
                                {...chartOptionsGeneratorSettings}
                                useUTC={true}
                            />
                        )}
                    </WithLoadingTable>
                </ChartWrapper>
            );
        });
    }, [extendedReportingObjectsById, localCurrentOptions?.mainPeriod?.id, lang, visitorsInsideText, t]);

    return <Wrapper>{charts}</Wrapper>;
};

export default VisitorsInside;
